/**
 * CYP ASD Seed Data — Realistic dummy data for mockup demonstrations.
 *
 * Patient profile: "Alex M.", 9-year-old male referred by school SENCO and
 * paediatrician for social communication difficulties, rigid behaviours, and
 * sensory sensitivities. Parents report longstanding concerns since age 3.
 *
 * The clinical picture is broadly consistent with ASD — clear evidence on most
 * Criterion A items, mixed B items, functional impairment across domains.
 * The DDH (SSI) and Observation assessments reach the same conclusion from
 * different angles, with minor discrepancies that make the MDT discussion
 * realistic (e.g. B1 is clearer in observation than in parent report).
 *
 * All note text uses realistic clinical shorthand that a UK-based clinician
 * might write during a live assessment.
 */

// ═══════════════════════════════════════════════════════════════════════════
//  Helper — generate unique-ish IDs for note objects
// ═══════════════════════════════════════════════════════════════════════════

let _noteId = 1000;
function n(text, questions) {
  const note = { id: _noteId++, text, type: 'note' };
  if (questions) note.questions = questions;
  return note;
}

// ═══════════════════════════════════════════════════════════════════════════
//  1. DDH / SSI — allData (keyed by criterion ID + 'FI')
// ═══════════════════════════════════════════════════════════════════════════

export const SSI_ALL_DATA = {
  A1: {
    evidence_early: 'Clear evidence',
    evidence_current: 'Clear evidence',
    domains_early: ['social', 'emotional'],
    domains_current: ['social', 'educational', 'emotional'],
    notes_early: [
      n('Mum reports A did not point to share interest until ~2.5y — always pointed to request, never to show. No joint attention games (peek-a-boo, pat-a-cake) initiated by child.'),
      n('Nursery flagged at 3y that A played alongside peers but rarely engaged in reciprocal exchange. Would hand toy to adult but not wait for response or take turns.'),
      n('Parents describe "being in his own world" — would line up cars for extended periods, became distressed if another child moved them.'),
    ],
    notes_current: [
      n('In conversation A tends to monologue about Minecraft — does not check listener interest, rarely asks reciprocal questions. Teacher confirms same pattern in class.'),
      n('When asked "how was your weekend?" A gave factual list of events with no emotional commentary. Did not ask clinician about theirs.'),
      n('Mum notes A will approach peers but interaction quickly breaks down — he imposes his rules on games and walks away if not followed.'),
    ],
    asked_questions: [
      'How does your child show you things they find interesting?',
      'Does your child take turns in conversation?',
      'When your child was a toddler, did they bring things to show you?',
    ],
  },

  A2: {
    evidence_early: 'Clear evidence',
    evidence_current: 'Clear evidence',
    domains_early: ['social'],
    domains_current: ['social', 'educational'],
    notes_early: [
      n('Limited use of gesture in early years — parents cannot recall A waving bye-bye until ~3y. Nodding/head-shaking came late (~4y).'),
      n('Eye contact described as "fleeting" from infancy. Health visitor noted at 2y review. Would look at objects held up but not follow adult\'s gaze to distal targets.'),
      n('Facial expression often flat or incongruent — would smile while describing something upsetting. Parents initially attributed to shyness.'),
    ],
    notes_current: [
      n('Eye contact in session variable — better 1:1 but deteriorated during group observation. Tends to look past interlocutor or at objects in hand.'),
      n('Gestures limited to instrumental pointing. No descriptive gestures (e.g. showing size, miming actions). Speech prosody monotone with occasional volume spikes.'),
      n('Teacher reports difficulty reading A\'s emotional state — "he looks the same whether happy or upset."'),
    ],
    asked_questions: [
      'Does your child use gestures when communicating?',
      'How would you describe your child\'s eye contact?',
    ],
  },

  A3: {
    evidence_early: 'Clear evidence',
    evidence_current: 'Partial evidence',
    domains_early: ['social'],
    domains_current: ['social', 'community'],
    notes_early: [
      n('No imaginative play observed — functional play with vehicles only (push along track, crash). Did not assign roles or narratives to figures.'),
      n('At nursery, parallel play only. Staff noted A would sometimes watch peers but not join in. When placed in group activity, A stayed on periphery.'),
    ],
    notes_current: [
      n('A has 2 "friends" — both share Minecraft interest. Interaction is primarily online gaming; limited face-to-face contact outside school.'),
      n('A describes friendship in transactional terms: "Jake is my friend because he has a good Minecraft server." Struggled to describe what makes someone a friend beyond shared interest.'),
      n('Some evidence of desire for social connection — distressed when not invited to a birthday party. But does not adjust behaviour to maintain friendships.'),
    ],
    asked_questions: [
      'Does your child have friends?',
      'How does your child play with other children?',
      'Does your child understand the concept of friendship?',
    ],
  },

  B1: {
    evidence_early: 'Partial evidence',
    evidence_current: 'Partial evidence',
    domains_early: [],
    domains_current: ['educational'],
    notes_early: [
      n('Parents report some echolalia at 2-3y — would repeat last 2-3 words of adult sentences. Resolved by ~4y.'),
      n('No clear motor stereotypies recalled, though dad describes occasional hand-flapping when excited that "all toddlers do." No lining up beyond the car play.'),
    ],
    notes_current: [
      n('Some scripted speech observed — uses phrases from YouTube videos in conversation, sometimes contextually inappropriate. Teacher notes "quirky expressions."'),
      n('No overt motor stereotypies in session. Parents report finger-flicking when anxious, particularly before school.'),
    ],
    asked_questions: [
      'Does your child repeat phrases or words out of context?',
      'Have you noticed any repetitive movements?',
    ],
  },

  B2: {
    evidence_early: 'Clear evidence',
    evidence_current: 'Clear evidence',
    domains_early: ['home'],
    domains_current: ['home', 'educational'],
    notes_early: [
      n('Severe distress with routine changes from ~18m. Would scream if taken different route to nursery. Insisted on same plate, cup, spoon — parents had to buy duplicates.'),
      n('Transition difficulties significant — nursery used visual timetable from age 3. Without it, frequent meltdowns.'),
    ],
    notes_current: [
      n('Still requires advance warning for any change. Supply teachers = meltdown. Family holidays require weeks of preparation and "social stories."'),
      n('Rigid bedtime routine — exact sequence of 7 steps. If disrupted, A restarts from beginning. Takes 45min-1hr.'),
      n('School reports difficulty with timetable changes, fire drills cause extreme distress lasting 30+ min.'),
    ],
    asked_questions: [
      'How does your child react to changes in routine?',
      'Does your child insist on things being done in a particular way?',
    ],
  },

  B3: {
    evidence_early: 'Clear evidence',
    evidence_current: 'Clear evidence',
    domains_early: ['home'],
    domains_current: ['home', 'educational', 'social'],
    notes_early: [
      n('Intense interest in trains from 2y — could name every Thomas character, knew routes of local rail network by 4y. Would only read train books. Interest lasted ~3 years before shifting to space.'),
    ],
    notes_current: [
      n('Currently fixated on Minecraft — plays 3-4hrs daily if not limited. Conversation revolves almost exclusively around the game. Brought notebook of Minecraft builds to session and narrated for 20min without pause.'),
      n('School reports A completes work quickly so he can return to drawing Minecraft scenes. Homework battles at home when topic is not related to interest.'),
      n('Interest intensity clearly abnormal for age — peers play Minecraft but can shift topics. A cannot.'),
    ],
    asked_questions: [
      'Does your child have any intense interests?',
      'How much time does your child spend on their interests?',
    ],
  },

  B4: {
    evidence_early: 'Partial evidence',
    evidence_current: 'Clear evidence',
    domains_early: ['home', 'selfCare'],
    domains_current: ['home', 'selfCare', 'community'],
    notes_early: [
      n('Feeding difficulties from weaning — refused textured foods until ~3y. Limited diet of ~10 foods attributed to "fussiness" at the time.'),
      n('Would cover ears at hoover, hand dryer, flushing toilets. Parents avoided these situations rather than addressing.'),
    ],
    notes_current: [
      n('Diet still restricted — approx 12 foods, all must be specific brands. Gags on new textures. Referred to dietitian at age 7.'),
      n('Wears same soft joggers every day — refuses jeans, buttons, zips, seams on socks. School uniform is source of daily conflict.'),
      n('Covers ears in assembly, canteen, PE hall. Has ear defenders but refuses to use them (draws attention). Recently started requesting to eat lunch alone.'),
      n('Seeks deep pressure — wraps in blanket, squeezes into small spaces. Uses weighted blanket to sleep.'),
    ],
    asked_questions: [
      'Does your child have any sensory sensitivities?',
      'Are there particular textures or sounds that bother your child?',
      'Does your child seek out any particular sensory experiences?',
    ],
  },

  // Functional Impairment section
  FI: {
    social_evidence: 'Clear evidence',
    social_indicators: [
      'Struggles to make or keep friends',
      'Misunderstandings leading to conflict or withdrawal',
      'Relationships based only on shared interests',
    ],
    social_other: 'Friendships limited to online gaming partners. Face-to-face interaction minimal outside school.',

    educational_evidence: 'Clear evidence',
    educational_indicators: [
      'Missing instructions that rely on social cues',
      'Meltdowns or shutdowns triggered by social or sensory overload',
      'Distress in unstructured times (break/lunch)',
      'Interest interferes with schoolwork',
    ],
    educational_other: 'On school SEN register — 1:1 TA support 15hrs/wk. Visual timetable in place since Reception.',

    home_evidence: 'Clear evidence',
    home_indicators: [
      'Rigid routines causing conflict or distress when disrupted',
      'Emotional outbursts during transitions',
      'Family adapting daily life to avoid triggers',
      'Interest interferes with responsibilities',
    ],
    home_other: 'Parents describe "walking on eggshells." Siblings report feeling secondary. Significant parental stress.',

    emotional_evidence: 'Clear evidence',
    emotional_indicators: [
      'High anxiety around social situations or unpredictability',
      'Emotional exhaustion after school',
      'Shutdowns, meltdowns, withdrawal, or avoidance',
      'Low self-esteem from repeated social failures',
    ],
    emotional_other: 'After-school "crash" — A needs 1hr alone in room before can interact with family. Describes himself as "weird."',

    community_evidence: 'Partial evidence',
    community_indicators: [
      'Overwhelm or distress in busy or noisy environments',
      'Limited participation in age-typical leisure activities',
    ],
    community_other: 'Stopped attending Scouts after 3 sessions — too noisy. Attends Minecraft club (online) only.',

    selfCare_evidence: 'Partial evidence',
    selfCare_indicators: [
      'Challenges with washing, brushing teeth, or hair care',
      'Distress around clothing textures or temperature changes',
      'Restricted eating linked to sensory issues',
    ],
    selfCare_other: 'Requires daily prompting for hygiene. Clothing battles every morning. Diet restriction managed by dietitian.',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
//  2. DDH / SSI Review — persisted under `cyp-asd-ssi-review-${patientId}`
// ═══════════════════════════════════════════════════════════════════════════

export const SSI_REVIEW_DATA = {
  decisions: { A: true, B: true, C: true, D: true, E: true },
  confidence: 80,
  confidenceTernary: 'likely',
  confidenceStyle: 'ternary',
  formulationNotes: [
    n('Overall clinical impression: Hx strongly consistent w/ ASD. Social communication deficits present from early developmental period and pervasive across contexts — not situation-specific, ruling out primary social anxiety as explanation.'),
    n('DDx considered — Social Anxiety Disorder: Significant anxiety in social situations but this is secondary to genuine social communication deficits. Anxiety onset followed social failures (not vice versa). No anticipatory anxiety about evaluation; more confused than fearful.'),
    n('DDx considered — ADHD: Some inattentive features (poor sustained attention in class, loses belongings, impulsive in conversation). However hyper-focus on interests argues against global attentional deficit. Not meeting full criteria per Conners parent/teacher. Recommend monitoring 6-12m post-dx.'),
    n('DDx ruled out — Intellectual Disability: FSIQ 104 (WISC-V, age 7). Academic attainment broadly age-appropriate. Language development WNL per SALT discharge age 5.'),
    n('DDx ruled out — Reactive Attachment Disorder: Stable family home since birth, secure attachment to both parents. No trauma hx, no neglect. Social difficulties not attachment-driven.'),
    n('Strengths identified: Strong visual-spatial skills (Minecraft builds are complex and creative). Excellent rote memory (rail network, game mechanics). Honest and rule-following. Parents are engaged, well-informed, and proactive advocates.'),
    n('Protective factors: Supportive school w/ SEN infrastructure already in place. Has at least 2 peer connections (online). No current safeguarding concerns. Family well-resourced.'),
  ],
  formulationConditions: [
    'Autism Spectrum Disorder (ASD)',
    'Attention Deficit Hyperactivity Disorder (ADHD)',
    'Social Anxiety Disorder',
  ],
  formulationConditionNotes: {
    'Autism Spectrum Disorder (ASD)': [
      n('All 3 Criterion A domains met across both EDP and current presentation. At least 2 Criterion B domains met (B2, B3 clear; B4 clear current; B1 partial). Sx present from early developmental period w/ functional impairment across multiple domains.'),
      n('Presentation consistent w/ Level 1 ASD — "requiring support." Verbal, average IQ, some peer relationships (albeit narrow).'),
    ],
    'Attention Deficit Hyperactivity Disorder (ADHD)': [
      n('Some inattentive features noted — poor sustained attention in class, difficulty with multi-step instructions, loses belongings frequently. However, hyper-focus on interests argues against global attentional deficit.'),
      n('Recommend monitoring. If ASD dx confirmed, reassess ADHD traits in 6-12m to determine whether they are better explained by ASD-related executive function difficulties or represent comorbid ADHD.'),
    ],
    'Social Anxiety Disorder': [
      n('Significant anxiety in social situations but preceded by and attributable to genuine social communication difficulties rather than fear of negative evaluation per se. Anxiety is secondary to ASD-related social confusion rather than a primary anxiety disorder.'),
    ],
  },
  customConditions: [],
};

// ═══════════════════════════════════════════════════════════════════════════
//  3. Observation — stored under `cyp-asd-obs-${patientId}`
// ═══════════════════════════════════════════════════════════════════════════

export const OBS_STATE = {
  assessmentMode: 'preset',
  selectedTaskIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  activeTaskId: 1,
  activeView: 'task',
  activeGeneralPanel: null,
  showObservations: false,
  confidenceStyle: 'ternary',
  allTaskData: {
    // Task 1: Description of a Picture
    1: {
      dsm_A1_evidence: 'Clear evidence',
      dsm_A1_notes: [
        n('Described picture in factual terms only — "there is a dog, a house, a tree." No narrative, no inference about what characters might be feeling or doing.'),
        n('Did not spontaneously share reactions or ask clinician what they noticed. Required direct prompting to continue.'),
      ],
      dsm_A2_evidence: 'Clear evidence',
      dsm_A2_notes: [
        n('Eye contact minimal during task — gaze fixed on picture throughout. When looked up, gaze directed past clinician\'s face.'),
        n('No pointing at picture elements — used whole-hand indication. Facial expression flat throughout.'),
      ],
      dsm_B3_evidence: 'Partial evidence',
      dsm_B3_notes: [
        n('Quickly diverted to Minecraft: "this house looks like a Minecraft build." Spent 2min comparing picture to game without prompting.'),
      ],
    },

    // Task 2: Telling a Story from a Book
    2: {
      dsm_A1_evidence: 'Clear evidence',
      dsm_A1_notes: [
        n('Read text literally w/o embellishment. Did not comment on characters\' emotions or motivations. When asked "how do you think Bear feels?" — "I don\'t know, it doesn\'t say."'),
      ],
      dsm_B2_evidence: 'Partial evidence',
      dsm_B2_notes: [
        n('Insisted on reading every page in order despite instruction to "tell the story in your own words." Corrected clinician who skipped a page.'),
      ],
      dsm_B3_evidence: 'Clear evidence',
      dsm_B3_notes: [
        n('After story, immediately related book content to Minecraft lore. Sustained monologue for 4min re: similarities between story\'s forest and a Minecraft biome.'),
      ],
    },

    // Task 3: Demonstration
    3: {
      dsm_A2_evidence: 'Clear evidence',
      dsm_A2_notes: [
        n('During tooth-brushing demo, used object only — no gestures to supplement explanation. Did not check clinician was following. Monotone delivery.'),
      ],
      dsm_B1_evidence: 'Clear evidence',
      dsm_B1_notes: [
        n('Repeated brushing motion in exact same pattern 6x despite having completed the demonstration. Appeared to enjoy the repetition itself.'),
      ],
      dsm_B2_evidence: 'Partial evidence',
      dsm_B2_notes: [
        n('Became anxious when asked to demonstrate a different activity — "but I already did one." Required reassurance that the rules allowed a second demonstration.'),
      ],
    },

    // Task 4: Conversation and Reporting
    4: {
      dsm_A1_evidence: 'Clear evidence',
      dsm_A1_notes: [
        n('Conversation entirely one-directional. A spoke at length about weekend (Minecraft session, then more Minecraft). Asked zero reciprocal questions across 8min interaction.'),
        n('When clinician introduced new topic (holiday plans), A gave 1-word answer then redirected to Minecraft within 15 seconds.'),
      ],
      dsm_B1_evidence: 'Partial evidence',
      dsm_B1_notes: [
        n('Used several scripted phrases from YouTube gamers — "that\'s totally OP" and "massive L" — in conversational contexts where they were semantically inappropriate.'),
      ],
      dsm_B3_evidence: 'Clear evidence',
      dsm_B3_notes: [
        n('Every topic eventually returned to Minecraft. A appeared unable to sustain conversation on any other subject beyond 2-3 exchanges.'),
      ],
    },

    // Task 5: Social Difficulties and Annoyance
    5: {
      questionsAsked: [0, 1, 2, 3, 4, 5],
      dsm_A1_evidence: 'Clear evidence',
      dsm_A1_notes: [
        n('Described bullying in matter-of-fact tone without apparent distress: "they call me weird but I am weird so it\'s accurate."'),
        n('Could not identify why peers might be annoyed by his behaviour. Showed limited insight into social impact of his monologuing.'),
      ],
      dsm_A3_evidence: 'Partial evidence',
      dsm_A3_notes: [
        n('Expressed desire to have friends but described friendship in purely functional terms. Could not articulate emotional aspects of friendship.'),
      ],
    },

    // Task 6: Emotions
    6: {
      dsm_A1_evidence: 'Partial evidence',
      dsm_A1_notes: [
        n('Identified basic emotions (happy, sad, angry) but struggled with nuanced emotions (embarrassed, proud, jealous). Defined "embarrassed" as "when you fall over."'),
      ],
      dsm_A3_evidence: 'Partial evidence',
      dsm_A3_notes: [
        n('All emotion examples were solitary/object-focused: happy = "when I play Minecraft," sad = "when WiFi goes down." No social-emotional examples generated.'),
      ],
    },

    // Task 7: Cartoons
    7: {
      dsm_A1_evidence: 'Clear evidence',
      dsm_A1_notes: [
        n('Retold cartoon factually w/o inferring characters\' intentions. Missed the humour entirely — when prompted "what\'s funny?" replied "I don\'t think it\'s funny."'),
      ],
      dsm_A3_evidence: 'Clear evidence',
      dsm_A3_notes: [
        n('Could not explain why character A tricked character B. Did not attribute mental states to characters (no theory of mind evident in narrative).'),
      ],
    },

    // Task 8: Friends, Relationships and Marriage
    8: {
      questionsAsked: [0, 1, 2, 3, 4],
      dsm_A1_evidence: 'Clear evidence',
      dsm_A1_notes: [
        n('"Jake is my friend because he has a good server." When asked what being a friend means: "someone who plays with you." No mention of trust, caring, shared feelings.'),
      ],
      dsm_A3_evidence: 'Clear evidence',
      dsm_A3_notes: [
        n('Re: marriage — "I don\'t want to get married, it sounds annoying." Could not explain why people might want a partner beyond practical reasons ("they help with cooking").'),
        n('Where do you want to live when older? "By myself so nobody moves my stuff." Showed no awareness of loneliness in this scenario.'),
      ],
    },

    // Task 9: Loneliness
    9: {
      questionsAsked: [0, 1, 2],
      dsm_A1_evidence: 'Partial evidence',
      dsm_A1_notes: [
        n('"I don\'t feel lonely because I have Minecraft." When pressed — "sometimes at lunch because nobody wants to talk about what I want to talk about."'),
      ],
      dsm_A3_evidence: 'Clear evidence',
      dsm_A3_notes: [
        n('Does not think other children feel lonely: "why would they? They have friends." Shows limited perspective-taking about peers\' emotional lives.'),
        n('Strategy for loneliness: "I just go on my iPad." No social strategies identified.'),
      ],
    },

    // Task 10: Creating a Story
    10: {
      dsm_A1_evidence: 'Partial evidence',
      dsm_A1_notes: [
        n('Story lacked social narrative — characters did not interact. Objects were described by properties (colour, size) rather than given roles or relationships.'),
      ],
      dsm_B2_evidence: 'Clear evidence',
      dsm_B2_notes: [
        n('Arranged objects in precise line before starting. Became distressed when clinician accidentally moved one. Restarted arrangement twice.'),
      ],
      dsm_B3_evidence: 'Clear evidence',
      dsm_B3_notes: [
        n('Story was a Minecraft recreation — "this is Steve and this is a Creeper." Used functional objects as game items rather than creating original narrative.'),
      ],
    },

    // General Observations — Self-reported (CAT-Q)
    general_self_reported: {
      asked_questions: [
        'I have developed a script to follow in social situations.',
        'I will repeat phrases that I have heard others say in the exact same way that I first heard them.',
        'In my own social interactions, I use behaviours that I have learned from watching other people interacting.',
        'I have tried to improve my understanding of social skills by watching other people.',
        'I have spent time learning social skills from television shows and films, and try to use these in my interactions.',
        'In social situations, I feel like I\'m \'performing\' rather than being myself.',
        'I need the support of other people in order to socialise.',
        'I don\'t feel the need to make eye contact with other people if I don\'t want to.',
      ],
      notes: [
        n('A shows emerging awareness of social scripts — describes learning "what to say" from YouTube. When asked if he follows rules in conversation: "I know you\'re supposed to ask how someone is but I don\'t know what to do with the answer."'),
        n('Echolalic compensation: repeats gamer phrases verbatim in social contexts. Appears to use these as social "templates" rather than generating spontaneous responses. Not clear if A recognises this as a strategy or if it\'s automatic.'),
        n('Limited evidence of masking in the traditional sense — A does not appear to monitor or adjust facial expressions. Mum reports he is "the same everywhere" which suggests low masking. This is consistent with his age (9y) — masking behaviours typically increase in adolescence.'),
        n('Re: eye contact — A stated matter-of-factly that he doesn\'t see the point of looking at people\'s eyes: "I can hear them without looking." No distress about this, no awareness it might be expected.'),
        n('Strengths: A is self-aware to a degree unusual for his age — articulated that he finds social situations "confusing but not scary." This suggests cognitive understanding of his differences even if pragmatic skills are limited.'),
        n('Parent corroborates: A does not appear to mask at school. Teacher reports same presentation as at home. This consistency supports genuine ASD presentation rather than context-dependent social anxiety.'),
      ],
    },

    // General Observations — Sensory (B4)
    general_sensory: {
      dsm_B4_evidence: 'Clear evidence',
      asked_questions: [
        'Are there any sensory sensitivities noticed (e.g. sound, light, touch, smell, taste, movement)?',
        'Have these sensory differences been present since early childhood, or did they emerge later?',
        'How do they respond to loud or sudden noises (e.g. alarms, hand dryers, fireworks)?',
        'Do they cover their ears, leave the room, or become distressed?',
        'Do they use coping strategies (e.g. ear defenders, headphones, avoiding certain places)?',
        'Are they sensitive to clothing textures, labels, seams, socks, or certain fabrics?',
        'Are there strong preferences or aversions related to food textures, tastes, or temperatures?',
        'Is their diet limited due to sensory reasons rather than appetite?',
        'Do they avoid or seek movement (e.g. rocking, pacing, spinning)?',
        'Do they notice when they are hungry, thirsty, tired, hot/cold, or need the toilet?',
        'How do sensory differences affect school, work, social situations, or routines?',
        'Do sensory issues contribute to meltdowns, shutdowns, avoidance, or anxiety?',
      ],
      notes: [
        n('Auditory: Significant hyperreactivity to sudden/loud noises — covers ears w/ hands, becomes visibly distressed. Hand dryers & school fire alarm are specific triggers. Mum carries ear defenders in bag at all times. Background noise (classroom chatter) impairs concentration per teacher report.'),
        n('Tactile: Restricted clothing tolerance — only wears soft cotton, no labels/seams. Refuses new shoes until "broken in" by mum wearing them first. Haircuts require advance preparation (social story + iPad distraction). Nail cutting requires sleep or deep distraction.'),
        n('Food/taste: Highly restricted diet — beige foods only (toast, chips, plain pasta, chicken nuggets). Refuses foods that touch on plate. Gags at strong smells (cooked vegetables, perfume). Dietitian involved since age 5. Diet limited due to texture/smell rather than appetite — A says "the orange foods feel wrong in my mouth."'),
        n('Vestibular: Seeks movement — rocks on chair at school, paces when thinking, spins on office chair during this session. Loves swings (could swing for >30min). No avoidance of heights or balance activities — appears hyposensitive in this modality.'),
        n('Interoception: Poor awareness of hunger/thirst — mum sets timer reminders. Does not recognise early signs of overwhelm until meltdown threshold reached. Temperature regulation poor — wears shorts in winter, doesn\'t report being cold.'),
        n('Impact: Sensory profile significantly impacts daily functioning. Meltdowns at school triggered primarily by auditory overload (assemblies, dining hall). Avoids birthday parties, shopping centres, cinemas. Family routines heavily adapted around sensory needs.'),
        n('Pattern: Consistent w/ mixed hyper-/hyposensitivity profile commonly seen in ASD. Hyperreactive in auditory, tactile, olfactory/gustatory. Hyposensitive in vestibular and interoceptive. Present since early childhood per parent — not acquired or fluctuating.'),
      ],
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
//  4. Observation Review — stored under `cyp-asd-obs-review-${patientId}`
// ═══════════════════════════════════════════════════════════════════════════

export const OBS_REVIEW_DATA = {
  confidence: 80,
  confidenceTernary: 'very_likely',
  formulationNotes: [
    n('Behavioural observation strongly supports ASD dx. Social communication deficits pervasive across all 10 tasks — A consistently failed to share interest, take conversational turns, or infer others\' mental states.'),
    n('Criterion A picture: Reciprocity deficits (A1) most prominent — monologuing, zero reciprocal questions, no checking listener interest. Nonverbal deficits (A2) clear — flat affect, minimal gesture, poor eye contact. Relationship understanding (A3) concrete and egocentric.'),
    n('Criterion B picture: Restricted interest in Minecraft dominated every task — A redirected within seconds regardless of topic. Rigidity (B2) observed in story-telling, demonstration, and creating-a-story tasks. Stereotyped behaviour (B1) noted in demonstration task (repetitive motor pattern). Sensory profile not directly assessed in observation but parent report + dietitian involvement corroborate.'),
    n('No evidence of oppositional or conduct difficulties — A was compliant, cooperative, and polite throughout. Challenging behaviours (distress at object displacement) were clearly anxiety-driven, not volitional.'),
    n('Strengths observed: Excellent verbal fluency on preferred topics. Good vocabulary and sentence structure. Able to sustain attention for extended periods when interested. Creative within his interest domain (elaborate Minecraft narratives). Polite and rule-following.'),
    n('ADHD-related observations: Attention on non-preferred tasks dropped markedly — fidgety, looked away, gave minimal responses. However, sustained attention on Minecraft topics was exceptional (4-20min monologues). This interest-driven attention profile is more consistent w/ ASD than ADHD but warrants monitoring.'),
  ],
  formulationConditions: [
    'Autism Spectrum Disorder (ASD)',
    'Attention Deficit Hyperactivity Disorder (ADHD)',
  ],
  formulationConditionNotes: {
    'Autism Spectrum Disorder (ASD)': [
      n('Clear evidence across all Criterion A domains from observation alone. B2 (sameness) and B3 (restricted interests) unambiguous. B1 (stereotyped behaviour) observed in demonstration task. Overall presentation strongly indicative of ASD.'),
      n('Pragmatic language profile consistent w/ ASD — literal interpretation, monologue style, poor inference. Structural language (grammar, vocabulary) appeared age-appropriate.'),
    ],
    'Attention Deficit Hyperactivity Disorder (ADHD)': [
      n('Sustained attention on preferred topics was excellent (Minecraft monologues 4-20min). Attention on non-preferred tasks dropped quickly. Pattern more consistent with ASD interest-driven attention than ADHD-type attentional deficit. Recommend monitoring post-dx.'),
    ],
  },
  customConditions: [],
};

// ═══════════════════════════════════════════════════════════════════════════
//  5. MDT Review — stored under `cyp-asd-mdt-${patientId}`
// ═══════════════════════════════════════════════════════════════════════════

export const MDT_REVIEW_DATA = {
  decisions: { A: true, B: true, C: true, D: true, E: true },
  diagnosticNotes: [
    n('MDT consensus: ASD diagnosis confirmed (ICD-11 6A02, DSM-5 299.00). All five diagnostic criteria met.'),
    n('Criterion A: Persistent deficits in social communication and social interaction across multiple contexts — evidenced in both DDH and behavioural observation. Deficits in social-emotional reciprocity (A1), nonverbal communicative behaviours (A2), and developing/maintaining relationships (A3) all clearly demonstrated.'),
    n('Criterion B: At least 2 of 4 met — insistence on sameness (B2) and restricted interests (B3) strongest. Sensory reactivity (B4) also met based on parent report and dietitian referral hx. Stereotyped behaviour (B1) partial but present.'),
    n('Criterion C: Symptoms present from early developmental period — documented concerns from 18m (routine rigidity) and 2y (limited joint attention, restricted play).'),
    n('Criterion D: Functional impairment clearly demonstrated across social, educational, home, and emotional domains. SEN support in place since Reception.'),
    n('Criterion E: Not better explained by ID (FSIQ 104) or global developmental delay. Language WNL (SALT discharge age 5). Social anxiety secondary to ASD-related social confusion.'),
    n('Recommendations: Formal ASD dx, referral to post-diagnostic support pathway, school to update EHCP, ADHD monitoring review in 6-12m, continue dietitian input for ARFID-like presentation.'),
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
//  Empty defaults — what each page looks like when demo mode is OFF.
//  The user starts with a blank canvas and makes their own decisions.
// ═══════════════════════════════════════════════════════════════════════════

export const EMPTY_SSI_ALL_DATA = {};

export const EMPTY_SSI_REVIEW_DATA = {
  decisions: {},
  confidence: null,
  confidenceTernary: null,
  confidenceStyle: 'ternary',
  formulationNotes: [],
  formulationConditions: [],
  formulationConditionNotes: {},
};

export const EMPTY_OBS_STATE = {
  assessmentMode: 'preset',
  selectedTaskIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  activeTaskId: 1,
  activeView: 'task',
  activeGeneralPanel: null,
  showObservations: false,
  confidenceStyle: 'ternary',
  allTaskData: {},
};

export const EMPTY_OBS_REVIEW_DATA = {
  confidence: null,
  confidenceTernary: null,
  formulationNotes: [],
  formulationConditions: [],
  formulationConditionNotes: {},
};

export const EMPTY_MDT_REVIEW_DATA = {
  decisions: {},
  diagnosticNotes: [],
};

// ═══════════════════════════════════════════════════════════════════════════
//  Demo mode helpers
//
//  `isDemoMode(patientId)` — reads the toggle state from localStorage.
//  `setDemoMode(patientId, on)` — writes demo data or empty defaults into
//    ALL five localStorage keys at once so every page is consistent.
//  `ensureSeedData(patientId)` — legacy convenience wrapper, now only
//    seeds if demo mode is already ON (used by Review/MDT pages that
//    don't have their own toggle but should show data if demo is active).
// ═══════════════════════════════════════════════════════════════════════════

const SEED_VERSION = 4;

export function isDemoMode(patientId) {
  return localStorage.getItem(`cyp-asd-demo-${patientId}`) === '1';
}

/**
 * Flip demo mode on/off and immediately write the corresponding data
 * (seed or empty) into all five localStorage keys so every CYP ASD
 * page is in sync. Returns the new demo-mode boolean.
 */
export function setDemoMode(patientId, on) {
  const demoKey = `cyp-asd-demo-${patientId}`;
  const backupPrefix = `cyp-asd-backup-`;

  const storageKeys = [
    `cyp-asd-ssi-${patientId}`,
    `cyp-asd-ssi-review-${patientId}`,
    `cyp-asd-obs-${patientId}`,
    `cyp-asd-obs-review-${patientId}`,
    `cyp-asd-mdt-${patientId}`,
  ];

  const demoData = [SSI_ALL_DATA, SSI_REVIEW_DATA, OBS_STATE, OBS_REVIEW_DATA, MDT_REVIEW_DATA];

  try {
    if (on) {
      // Back up the user's current (manual) data before overwriting with demo content.
      // This lets us restore it when they flip back to non-demo mode.
      storageKeys.forEach(key => {
        const current = localStorage.getItem(key);
        if (current) localStorage.setItem(`${backupPrefix}${key}`, current);
      });

      storageKeys.forEach((key, i) => {
        localStorage.setItem(key, JSON.stringify(demoData[i]));
      });
      localStorage.setItem(`cyp-asd-seeded-${patientId}`, String(SEED_VERSION));
    } else {
      // Restore the user's backed-up manual data (or fall back to empty defaults
      // if no backup exists — e.g. the very first toggle-off after a fresh load).
      const emptyData = [EMPTY_SSI_ALL_DATA, EMPTY_SSI_REVIEW_DATA, EMPTY_OBS_STATE, EMPTY_OBS_REVIEW_DATA, EMPTY_MDT_REVIEW_DATA];

      storageKeys.forEach((key, i) => {
        const backup = localStorage.getItem(`${backupPrefix}${key}`);
        localStorage.setItem(key, backup || JSON.stringify(emptyData[i]));
        localStorage.removeItem(`${backupPrefix}${key}`);
      });
    }
  } catch { /* quota exceeded — ignore in mockup */ }

  localStorage.setItem(demoKey, on ? '1' : '0');
  return on;
}

/**
 * Legacy eager seeder — now only populates when demo mode is active.
 * Called by Review and MDT pages that don't own the toggle themselves.
 */
export function ensureSeedData(patientId) {
  if (!isDemoMode(patientId)) return;

  const sentinelKey = `cyp-asd-seeded-${patientId}`;
  const existing = localStorage.getItem(sentinelKey);
  if (existing && Number(existing) >= SEED_VERSION) return;

  const keys = [
    { key: `cyp-asd-ssi-${patientId}`,        data: SSI_ALL_DATA },
    { key: `cyp-asd-ssi-review-${patientId}`,  data: SSI_REVIEW_DATA },
    { key: `cyp-asd-obs-${patientId}`,         data: OBS_STATE },
    { key: `cyp-asd-obs-review-${patientId}`,  data: OBS_REVIEW_DATA },
    { key: `cyp-asd-mdt-${patientId}`,         data: MDT_REVIEW_DATA },
  ];

  try {
    keys.forEach(({ key, data }) => {
      localStorage.setItem(key, JSON.stringify(data));
    });
    localStorage.setItem(sentinelKey, String(SEED_VERSION));
  } catch { /* quota exceeded — ignore in mockup */ }
}
